export type ProductType = 'simple' | 'variable';
export type ShippingType = 'free' | 'paid';

export interface ProductMediaRef {
  id: string;
  imageSmall?: string;
  imageLarge?: string;
}

export interface ProductVariantAttributeValue {
  id: string;
  attributeId: string;
  name: string;
  slug: string;
  colorCode?: string;
}

export interface ProductVariant {
  id?: string;
  attributeValueIds: string[];
  attributeValues?: ProductVariantAttributeValue[];
  sku?: string;
  regularPrice: number;
  salePrice?: number;
  discount?: number;
  costPrice?: number;
  shippingCharge?: number;
  stock: number;
  imageMediaId?: string | null;
  image?: ProductMediaRef | null;
  isActive: boolean;
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  sku: string;
  productType: ProductType;
  brandIds: string[];
  categoryIds: string[];
  subcategoryIds: string[];
  collectionIds: string[];
  stateId?: string | null;
  districtId?: string | null;
  assemblyId?: string | null;
  constituencyId?: string | null;
  stateName?: string;
  districtName?: string;
  assemblyName?: string;
  constituencyName?: string;
  brandNames?: string[];
  categoryNames?: string[];
  subcategoryNames?: string[];
  collectionNames?: string[];
  shortDescription: string;
  description: string;
  manufacturingInformation?: string;
  returnExchangePolicy?: string;
  seriesName?: string;
  seriesNumber?: string;
  editor?: string;
  edition?: string;
  isbn?: string;
  doiUrl?: string;
  deliveryTime?: string;
  pageCount?: string;
  formatDetails?: string;
  firstPublished?: string;
  language?: string;
  imprint?: string;
  imprintValueId?: string | null;
  weight?: string;
  dimensions?: string;
  apaCitation?: string;
  chicagoCitation?: string;
  whatsInside?: string;
  editorialNote?: string;
  tableOfContents?: string;
  samplePdfUrl?: string;
  digitalSamplePdfUrl?: string;
  contentPdfUrl?: string;
  shippingType: ShippingType;
  shippingCharge?: number;
  tags: string[];
  taxClass: string;
  regularPrice?: number;
  salePrice?: number;
  discount?: number;
  costPrice?: number;
  stock?: number;
  totalStock?: number;
  minPrice?: number;
  maxPrice?: number;
  minSalePrice?: number;
  maxSalePrice?: number;
  trackInventory: boolean;
  attributeIds: string[];
  attributeNames?: string[];
  thumbnailMediaId?: string | null;
  imageMediaIds: string[];
  thumbnail?: ProductMediaRef | null;
  images?: ProductMediaRef[];
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string[];
  variants?: ProductVariant[];
  isActive: boolean;
  averageRating?: number;
  reviewCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedProducts {
  results: Product[];
  total: number;
  page: string | number;
  limit: string | number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export type ProductStatusFilter = 'all' | 'active' | 'inactive';

export interface ProductListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: 'active' | 'inactive';
  productTypes?: ProductType[];
  brandIds?: string[];
  categoryIds?: string[];
  imprintValueIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export interface ProductVariantPayload {
  id?: string;
  attributeValueIds: string[];
  sku?: string;
  regularPrice: number;
  salePrice?: number;
  discount?: number;
  costPrice?: number;
  shippingCharge?: number;
  stock?: number;
  imageMediaId?: string;
  isActive?: boolean;
}

export interface CreateProductPayload {
  name: string;
  slug?: string;
  productType?: ProductType;
  brandIds: string[];
  categoryIds: string[];
  subcategoryIds?: string[];
  collectionIds?: string[];
  stateId?: string;
  districtId?: string;
  assemblyId?: string;
  constituencyId?: string;
  shortDescription?: string;
  description?: string;
  manufacturingInformation?: string;
  returnExchangePolicy?: string;
  seriesName?: string;
  seriesNumber?: string;
  editor?: string;
  edition?: string;
  isbn?: string;
  doiUrl: string;
  deliveryTime?: string;
  pageCount?: string;
  formatDetails?: string;
  firstPublished?: string;
  language?: string;
  imprint?: string;
  imprintValueId?: string;
  weight?: string;
  dimensions?: string;
  apaCitation?: string;
  chicagoCitation?: string;
  whatsInside?: string;
  editorialNote?: string;
  tableOfContents?: string;
  samplePdfUrl?: string;
  digitalSamplePdfUrl?: string;
  contentPdfUrl?: string;
  shippingType?: ShippingType;
  shippingCharge?: number;
  tags?: string[];
  taxClass?: string;
  regularPrice?: number;
  salePrice?: number;
  discount?: number;
  costPrice?: number;
  stock?: number;
  trackInventory?: boolean;
  attributeIds?: string[];
  variants?: ProductVariantPayload[];
  thumbnailMediaId?: string;
  imageMediaIds?: string[];
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string[];
  isActive?: boolean;
}

export type UpdateProductPayload = Partial<CreateProductPayload>;

export interface ProductImportResult {
  created: number;
  updated: number;
  failed: number;
  errors: string[];
}

export interface BulkProductIdsPayload {
  ids: string[];
}

export interface BulkProductStatusPayload extends BulkProductIdsPayload {
  isActive: boolean;
}

export const TAX_CLASS_OPTIONS = [
  { label: 'None', value: '' },
  { label: 'Standard', value: 'standard' },
  { label: 'Reduced', value: 'reduced' },
  { label: 'Zero', value: 'zero' },
  { label: 'GST 5%', value: 'gst-5' },
  { label: 'GST 12%', value: 'gst-12' },
  { label: 'GST 18%', value: 'gst-18' },
];
